import { sound } from '../sound';
import type { SfxKey } from '../audio.gen';
import {
  addActor,
  after,
  between,
  budget,
  pick,
  rand,
  spawn,
  type Draw,
  type Particle,
  type ParticleInit,
} from './engine';
import { glow, glowSprite, spark, twinkle } from './shapes';

/**
 * Emitters the presets are built from: a local burst, a rain from the top, a rise from the bottom, fountains,
 * firework rockets, shock rings and soft light. Counts are given for a desktop and scaled to the device cap.
 */
export type Range = number | readonly [number, number];
const val = (r: Range): number => (typeof r === 'number' ? r : between(r[0], r[1]));

/** Where and with what a preset celebrates. */
export interface Stage {
  /** origin of the win (the winning cells, the cash-out button…) */
  x: number;
  y: number;
  W: number;
  H: number;
  /** the game's palette (or the caller's override: the winner's colour, the sector colour); #RRGGBB */
  pal: readonly string[];
  /** light accent for glints (#RRGGBB) */
  light: string;
  /** the theme's text colour: shapes that must read on dark and light (seagulls) */
  ink: string;
}

export interface Look {
  draw: Draw | readonly Draw[];
  cols?: readonly string[];
  col2?: readonly string[] | string;
  size?: Range;
  life?: Range;
  gravity?: number;
  drag?: number;
  wind?: number;
  sway?: Range;
  swayY?: Range;
  swayF?: Range;
  vr?: Range;
  flip?: Range;
  face?: boolean;
  add?: boolean;
  grow?: number;
  fadeIn?: number;
  fadeOut?: number;
  alpha?: number;
  data?: () => number;
  onEnd?: (p: Particle) => void;
}

function make(s: Stage, l: Look, x: number, y: number, vx: number, vy: number, delay = 0): Particle | null {
  const cols = l.cols ?? s.pal;
  const draw = Array.isArray(l.draw) ? pick(l.draw as readonly Draw[]) : (l.draw as Draw);
  const col2 = typeof l.col2 === 'string' ? l.col2 : l.col2 ? pick(l.col2) : s.light;
  const init: ParticleInit = {
    x,
    y,
    vx,
    vy,
    delay,
    draw,
    col: pick(cols),
    col2,
    size: val(l.size ?? [4, 7]),
    life: val(l.life ?? [0.7, 1]),
    gravity: l.gravity ?? 0,
    drag: l.drag ?? 1,
    wind: l.wind ?? 0,
    sway: val(l.sway ?? 0),
    swayY: val(l.swayY ?? 0),
    swayF: val(l.swayF ?? 3),
    vr: val(l.vr ?? 0),
    rot: rand() * Math.PI * 2,
    flip: val(l.flip ?? 0),
    face: l.face ?? false,
    add: l.add ?? false,
    grow: l.grow ?? 0,
    fadeIn: l.fadeIn ?? 0,
    fadeOut: l.fadeOut ?? 0.7,
    alpha: l.alpha ?? 1,
    data: l.data ? l.data() : Math.floor(rand() * 8),
    onEnd: l.onEnd ?? null,
  };
  return spawn(init);
}

export interface BurstOpts extends Look {
  n: number;
  speed: Range;
  /** direction (rad) and spread; default all around */
  angle?: number;
  spread?: number;
  /** start radius around the point */
  r?: number;
  /** spread the spawns over this many ms */
  over?: number;
}

/** Particles flying out of a point. */
export function burst(s: Stage, x: number, y: number, o: BurstOpts): void {
  const n = budget(o.n);
  const spread = o.spread ?? Math.PI * 2;
  const base = o.angle ?? 0;
  for (let i = 0; i < n; i++) {
    const a = base + (o.spread === undefined ? rand() * spread : (rand() - 0.5) * spread);
    const v = val(o.speed);
    const r = (o.r ?? 0) * rand();
    make(
      s,
      o,
      x + Math.cos(a) * r,
      y + Math.sin(a) * r,
      Math.cos(a) * v,
      Math.sin(a) * v,
      o.over ? (rand() * o.over) / 1000 : 0,
    );
  }
}

export interface RainOpts extends Look {
  n: number;
  /** spawn over this many ms */
  over: number;
  /** vertical speed (positive: down from the top; negative with from:'bottom': up) */
  vy: Range;
  vx?: Range;
  from?: 'top' | 'bottom' | 'left' | 'right';
  /** horizontal band (0..1 of the width) */
  band?: readonly [number, number];
  /** from top / bottom — part of the pieces already in the air this far into the screen (fading in); default 0.45 */
  fill?: number;
}

/** A rain over the screen (falling petals) or a rise from the bottom (bubbles, lanterns). */
export function rain(s: Stage, o: RainOpts): void {
  const n = budget(o.n);
  const [b0, b1] = o.band ?? [0, 1];
  for (let i = 0; i < n; i++) {
    const d = ((i / n) * o.over + rand() * 120) / 1000;
    const from = o.from ?? 'top';
    let x = s.W * between(b0, b1);
    const fill = o.fill ?? 0.45;
    const inAir = (from === 'top' || from === 'bottom') && fill > 0 && rand() < 0.55;
    let y =
      from === 'top'
        ? inAir
          ? between(-20, s.H * fill)
          : between(-60, -10)
        : inAir
          ? between(s.H * (1 - fill), s.H + 20)
          : s.H + between(10, 50);
    if (from === 'left' || from === 'right') {
      x = from === 'left' ? -between(20, 60) : s.W + between(20, 60);
      y = s.H * between(b0, b1);
    }
    const p = make(s, o, x, y, val(o.vx ?? 0), val(o.vy), inAir ? d * 0.4 : d);
    if (p && inAir) p.fadeIn = Math.max(p.fadeIn, 0.18);
  }
}

/** Soft additive light at a point (a "flash" that does not flicker). */
export function light(s: Stage, x: number, y: number, r: number, col: string, life = 0.6, alpha = 0.9): void {
  spawn({ x, y, draw: glow, col, size: r, life, add: true, alpha, fadeIn: 0.15, fadeOut: 0.3, grow: 0.4 });
}

/** A few twinkles around a point. */
export function glints(s: Stage, x: number, y: number, n: number, r: number, cols?: readonly string[]): void {
  burst(s, x, y, {
    n,
    speed: [10, 60],
    r,
    draw: twinkle,
    cols: cols ?? [s.light, ...s.pal.slice(0, 2)],
    size: [3, 7],
    life: [0.5, 0.9],
    vr: [-2, 2],
    add: true,
    fadeIn: 0.25,
    fadeOut: 0.5,
    over: 300,
  });
}

/** An expanding ring (shock wave, ripple). squash < 1 flattens it (water). */
export function shock(
  x: number,
  y: number,
  o: { col: string; r: number; dur: number; width?: number; squash?: number; delay?: number },
): void {
  addActor({
    dur: o.dur,
    delay: o.delay ?? 0,
    draw(g, k) {
      const e = 1 - Math.pow(1 - k, 3);
      g.globalAlpha = (1 - k) * 0.9;
      g.strokeStyle = o.col;
      g.lineWidth = (o.width ?? 4) * (1 - k * 0.7);
      g.beginPath();
      g.ellipse(x, y, Math.max(0.1, o.r * e), Math.max(0.1, o.r * e * (o.squash ?? 1)), 0, 0, Math.PI * 2);
      g.stroke();
    },
  });
}

/** A screen-wide soft wash of colour (radial, from a point): mega moments. */
export function wash(
  x: number,
  y: number,
  o: { col: string; r: number; dur: number; alpha?: number; delay?: number },
): void {
  addActor({
    dur: o.dur,
    delay: o.delay ?? 0,
    draw(g, k) {
      const sp = glowSprite(o.col);
      if (!sp) return;
      const a = k < 0.2 ? k / 0.2 : 1 - (k - 0.2) / 0.8;
      g.globalAlpha = Math.max(0, a) * (o.alpha ?? 0.5);
      g.globalCompositeOperation = 'lighter';
      const r = o.r * (0.7 + 0.3 * k);
      g.drawImage(sp, x - r, y - r, r * 2, r * 2);
    },
  });
}

let lastPop = 0;
/** A pop / boom with a rate limit, so a garland does not turn into noise. Silent while sound is off. */
export function pop(key: SfxKey = 'common/pop', volume = 0.45, gapMs = 90): void {
  const now = performance.now();
  if (now - lastPop < gapMs) return;
  lastPop = now;
  sound.play(key, { volume });
}

export interface FireworkOpts {
  col: string;
  n?: number;
  speed?: number;
  /** extra pieces mixed into the sparks (nuts, paper, blossoms) */
  pieces?: Look & { n: number };
  sound?: SfxKey | null;
  ring?: boolean;
  /** life of the sparks, s */
  life?: Range;
}

/** An explosion of sparks (with a glow, optional ring and themed pieces). */
export function explode(s: Stage, x: number, y: number, o: FireworkOpts): void {
  const sp = o.speed ?? 260;
  light(s, x, y, 70, o.col, 0.5, 0.8);
  burst(s, x, y, {
    n: o.n ?? 60,
    speed: [sp * 0.35, sp],
    draw: spark,
    cols: [o.col, o.col, s.light],
    size: [1.6, 2.6],
    life: o.life ?? [0.7, 1.2],
    gravity: 140,
    drag: 0.35,
    face: true,
    add: true,
    fadeOut: 0.55,
  });
  if (o.ring) {
    const n = budget(28);
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2;
      make(
        s,
        { draw: glow, cols: [o.col], size: 5, life: 0.9, drag: 0.3, gravity: 60, add: true },
        x,
        y,
        Math.cos(a) * sp * 0.9,
        Math.sin(a) * sp * 0.9,
      );
    }
  }
  if (o.pieces)
    burst(s, x, y, {
      speed: [sp * 0.3, sp * 0.8],
      drag: 0.5,
      gravity: 260,
      life: [1, 1.5],
      vr: [-6, 6],
      ...o.pieces,
    });
  if (o.sound !== null) pop(o.sound ?? 'common/boom', 0.35, 120);
}

/** A rocket rising from (x0, y0) to (x1, y1), then `then` at the top. */
export function launch(
  s: Stage,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  col: string,
  then: (x: number, y: number) => void,
  delayMs = 0,
  flight?: number,
): void {
  const go = () => {
    const t = flight ?? between(0.55, 0.75);
    const p = spawn({
      x: x0,
      y: y0,
      vx: (x1 - x0) / t,
      vy: (y1 - y0) / t,
      draw: spark,
      col,
      size: 2.6,
      life: t,
      face: true,
      add: true,
      fadeOut: 1,
      onEnd: (q) => then(q.x, q.y),
    });
    if (p) {
      // the trail: a few embers left behind
      for (let i = 1; i <= 5; i++)
        spawn({
          x: x0,
          y: y0,
          vx: p.vx * 0.98,
          vy: p.vy * 0.98,
          delay: i * 0.04,
          draw: glow,
          col,
          size: 5,
          life: t - i * 0.04,
          add: true,
          alpha: 0.4,
        });
      pop('common/pop', 0.3, 160);
    }
  };
  if (delayMs) after(delayMs, go);
  else go();
}

/** Ease helpers for the set pieces. */
export const ease = {
  out: (k: number) => 1 - Math.pow(1 - k, 3),
  inOut: (k: number) => (k < 0.5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2),
  back: (k: number) => {
    if (k <= 0) return 0;
    const c = 1.7;
    return 1 + (c + 1) * Math.pow(k - 1, 3) + c * Math.pow(k - 1, 2);
  },
  /** 0→1 over [a, b] of k */
  span: (k: number, a: number, b: number) => Math.min(1, Math.max(0, (k - a) / (b - a))),
  /** in / hold / out envelope */
  env: (k: number, inK = 0.12, outK = 0.8) =>
    k < inK ? k / inK : k > outK ? Math.max(0, 1 - (k - outK) / (1 - outK)) : 1,
};
